//
// # ParamFields
//
// Shared rendering + validation for a `GeneratorParamSpec` parameter form.
// Extracted from EditInstanceDialog so both the "Add/Edit Instance" dialog and
// the Properties Panel render generator params identically.
//
// Values are held as a `Record<string, string>` (raw input text). Use
// `convertParamValues` to validate + coerce them to typed values before
// sending to the server.
//

import React, { useCallback, useEffect, useRef, useState } from "react";
import { paramValueToString } from "CoreUtils";
import type { GeneratorParamSpec } from "ServerInterface";

export type ParamSpec = GeneratorParamSpec["params"][0];

// Check number constraints, returning error message or null if valid.
export function checkNumberConstraints(
  p: ParamSpec,
  num: number
): string | null {
  if (p.constraints?.min !== undefined && num < p.constraints.min) {
    return `Must be >= ${p.constraints.min}`;
  }
  if (p.constraints?.max !== undefined && num > p.constraints.max) {
    return `Must be <= ${p.constraints.max}`;
  }
  return null;
}

// Check enum constraints, returning error message or null if valid.
export function checkEnumConstraints(
  p: ParamSpec,
  value: string
): string | null {
  if (p.constraints?.enum && !p.constraints.enum.map(String).includes(value)) {
    return `Must be one of: ${p.constraints.enum.join(", ")}`;
  }
  return null;
}

// Validate + coerce raw string param values to typed values.
export function convertParamValues(
  paramSpec: GeneratorParamSpec,
  paramValues: Record<string, string>
): { ok: true; val: Record<string, unknown> } | { ok: false; error: string } {
  const typed: Record<string, unknown> = {};
  for (const p of paramSpec.params) {
    const raw = paramValues[p.name] ?? "";
    if (p.type === "number") {
      if (raw === "") {
        // Use default if available
        if (p.default !== undefined) {
          typed[p.name] = p.default;
          continue;
        }
        return { ok: false, error: `Parameter "${p.name}" requires a number` };
      }
      const num = Number(raw);
      if (isNaN(num)) {
        return {
          ok: false,
          error: `Parameter "${p.name}": "${raw}" is not a valid number`,
        };
      }
      const constraintError = checkNumberConstraints(p, num);
      if (constraintError) {
        return { ok: false, error: `Parameter "${p.name}" ${constraintError}` };
      }
      typed[p.name] = num;
    } else if (p.type === "boolean") {
      typed[p.name] = raw === "true";
    } else if (p.type === "enum") {
      const constraintError = checkEnumConstraints(p, raw);
      if (constraintError) {
        return { ok: false, error: `Parameter "${p.name}" ${constraintError}` };
      }
      typed[p.name] = raw;
    } else {
      typed[p.name] = raw;
    }
  }
  return { ok: true, val: typed };
}

// Whether a converted value just restates the spec's own default. Stringified
// on both sides, so a numeric field whose spec default is `"1"` matches the `1`
// `convertParamValues` produced. A non-primitive counts as different.
function sameAsDefault(p: ParamSpec, value: unknown): boolean {
  if (p.default === undefined) return false;
  const a = paramValueToString(value);
  const b = paramValueToString(p.default);
  return !a.err && !b.err && a.val === b.val;
}

// The params worth sending: those that differ from the spec's default.
//
// `convertParamValues` emits an entry for every param in the spec, so sending
// its output verbatim stores the whole set on the instance — up to 327 params,
// 3.5-7KB per placement, which is what `instanceBuilder`'s `instanceParams`
// was slimmed down to avoid.
//
// `stored` is what the instance already has (edit, not create). Those keys
// ride along even at the default: `instanceBuilder.update` MERGES rather than
// replaces, so dropping one the user just reset would leave the old value.
export function omitDefaultParams(
  paramSpec: GeneratorParamSpec,
  typed: Record<string, unknown>,
  stored?: Readonly<Record<string, unknown>> | null
): Record<string, unknown> {
  const specByName = new Map(paramSpec.params.map((p) => [p.name, p]));
  const out: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(typed)) {
    if (stored !== undefined && stored !== null && name in stored) {
      out[name] = value;
      continue;
    }
    const p = specByName.get(name);
    // A param the spec doesn't declare has no default to be equal to.
    if (p === undefined || !sameAsDefault(p, value)) out[name] = value;
  }
  return out;
}

// Human-readable constraint summary for a param (min/max/default).
export function formatConstraintInfo(p: ParamSpec): string | null {
  const parts: string[] = [];
  if (p.constraints?.min !== undefined) parts.push(`min: ${p.constraints.min}`);
  if (p.constraints?.max !== undefined) parts.push(`max: ${p.constraints.max}`);
  if (p.default !== undefined) {
    const def = paramValueToString(p.default);
    if (!def.err) parts.push(`default: ${def.val}`);
  }
  return parts.length > 0 ? parts.join(", ") : null;
}

// Validate a single parameter value for real-time feedback.
export function validateParamValue(p: ParamSpec, raw: string): string | null {
  if (p.type === "number") {
    // Empty is OK if there's a default
    if (raw === "") {
      if (p.default !== undefined) return null;
      return "Required";
    }
    const num = Number(raw);
    if (isNaN(num)) return "Not a valid number";
    return checkNumberConstraints(p, num);
  } else if (p.type === "enum") {
    return checkEnumConstraints(p, raw);
  }
  return null;
}

export interface ParamFieldsProps {
  readonly paramSpec: GeneratorParamSpec;
  readonly paramValues: Record<string, string>;
  readonly setParamValues: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  // Optional ref to focus the first input (used by the dialog).
  readonly firstInputRef?: React.RefObject<HTMLInputElement>;
  // Optionally disable all inputs (e.g. while applying).
  readonly disabled?: boolean;
}

// The shared generator-parameter form.
export function ParamFields({
  paramSpec,
  paramValues,
  setParamValues,
  firstInputRef,
  disabled,
}: ParamFieldsProps) {
  // Track which fields are currently shaking (for animation).
  const [shakingFields, setShakingFields] = useState<Set<string>>(new Set());

  // Track mounted state to prevent state updates after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Trigger shake animation for a field.
  const triggerShake = useCallback((fieldName: string) => {
    setShakingFields((prev) => new Set(prev).add(fieldName));
    setTimeout(() => {
      if (mountedRef.current) {
        setShakingFields((prev) => {
          const next = new Set(prev);
          next.delete(fieldName);
          return next;
        });
      }
    }, 400);
  }, []);

  // Handle value change with validation feedback.
  const handleChange = useCallback(
    (p: ParamSpec, newValue: string) => {
      setParamValues((prev) => ({ ...prev, [p.name]: newValue }));
      // Check if newly entered value is invalid and trigger shake.
      const error = validateParamValue(p, newValue);
      if (error && newValue !== "") {
        triggerShake(p.name);
      }
    },
    [setParamValues, triggerShake]
  );

  return (
    <div className="add-instance-params">
      {paramSpec.params.map((p, idx) => {
        const raw = paramValues[p.name] ?? "";
        const error = validateParamValue(p, raw);
        const hasError = error !== null && raw !== "";
        const isShaking = shakingFields.has(p.name);

        const inputClassName = [
          "add-instance-param-input",
          hasError ? "add-instance-param-input-error" : "",
          isShaking ? "add-instance-param-input-shake" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div key={p.name} className="add-instance-param-field">
            <label className="add-instance-param-label">
              {p.name}
              {p.description && (
                <span className="add-instance-param-hint">
                  {" "}
                  — {p.description}
                </span>
              )}
            </label>
            {p.type === "enum" && p.constraints?.enum ? (
              <select
                className={inputClassName}
                value={raw}
                disabled={disabled}
                onChange={(e) => {
                  handleChange(p, e.target.value);
                }}
              >
                {p.constraints.enum.map((opt) => (
                  <option key={String(opt)} value={String(opt)}>
                    {String(opt)}
                  </option>
                ))}
              </select>
            ) : p.type === "boolean" ? (
              <select
                className="add-instance-param-input"
                value={raw || "false"}
                disabled={disabled}
                onChange={(e) => {
                  handleChange(p, e.target.value);
                }}
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : (
              <input
                ref={idx === 0 ? firstInputRef : undefined}
                className={inputClassName}
                type={p.type === "number" ? "number" : "text"}
                value={raw}
                min={p.constraints?.min}
                max={p.constraints?.max}
                disabled={disabled}
                onChange={(e) => {
                  handleChange(p, e.target.value);
                }}
              />
            )}
            {hasError ? (
              <span className="add-instance-param-error">{error}</span>
            ) : (
              formatConstraintInfo(p) && (
                <span className="add-instance-param-constraint">
                  {formatConstraintInfo(p)}
                </span>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}
